import React, { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
  Grid2 as Grid,
  Chip,
  Stack,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import HomePageTitle from "@components/Home/HomePageTitle/HomePageTitle";
import PageWrapper from "@components/Home/PageWrapper";
import PortalPageWrapper from "@components/Home/PortalPageWrapper";
import { GAME_IDS, type GameId, type GameLibraryItem } from "@models/game";
import { useGetLibraryQuery, useCreateSessionMutation } from "@store/gameApi";
import { useUpdatePatientMutation } from "@store/patientApi";
import { RootState } from "@store/store";
import { setSelectedPatient } from "@store/homeSlice";
import { gamePrimaryButtonSx } from "@components/Home/Games/gameUi";
import { resolveGameTimingMode } from "@utils/gameTiming";
import {
  buildImageCandidateUrls,
} from "@utils/gameImage";
import type { GameTimingMode } from "@models/patient";

const DOMAIN_KEYS = [
  "memory",
  "attention",
  "processing_speed",
  "language",
  "cognitive_flexibility",
  "spatial",
] as const;

type SortOption = "beneficial" | "recent" | "alphabetical";

/** Static game metadata when API has no data */
const GAME_META: Record<
  string,
  { nameKey: string; descriptionKey: string; domains: readonly string[] }
> = {
  [GAME_IDS.sequenceRecall]: {
    nameKey: "games.library.sequenceRecall",
    descriptionKey: "games.library.sequenceRecallDesc",
    domains: ["memory"],
  },
  [GAME_IDS.nBack]: {
    nameKey: "games.library.nBack",
    descriptionKey: "games.library.nBackDesc",
    domains: ["memory", "attention"],
  },
  [GAME_IDS.wordFluencyTap]: {
    nameKey: "games.library.wordFluencyTap",
    descriptionKey: "games.library.wordFluencyTapDesc",
    domains: ["language", "attention"],
  },
  [GAME_IDS.mathBlitz]: {
    nameKey: "games.library.mathBlitz",
    descriptionKey: "games.library.mathBlitzDesc",
    domains: ["processing_speed"],
  },
  [GAME_IDS.visualSearch]: {
    nameKey: "games.library.visualSearch",
    descriptionKey: "games.library.visualSearchDesc",
    domains: ["attention"],
  },
  [GAME_IDS.ruleSwitch]: {
    nameKey: "games.library.ruleSwitch",
    descriptionKey: "games.library.ruleSwitchDesc",
    domains: ["cognitive_flexibility"],
  },
  [GAME_IDS.patternMatch]: {
    nameKey: "games.library.patternMatch",
    descriptionKey: "games.library.patternMatchDesc",
    domains: ["memory"],
  },
  [GAME_IDS.orderTheSteps]: {
    nameKey: "games.library.orderTheSteps",
    descriptionKey: "games.library.orderTheStepsDesc",
    domains: ["memory", "cognitive_flexibility"],
  },
  [GAME_IDS.reactionTap]: {
    nameKey: "games.library.reactionTap",
    descriptionKey: "games.library.reactionTapDesc",
    domains: ["processing_speed"],
  },
  [GAME_IDS.sizeSort]: {
    nameKey: "games.library.sizeSort",
    descriptionKey: "games.library.sizeSortDesc",
    domains: ["spatial", "cognitive_flexibility"],
  },
  [GAME_IDS.soundMatch]: {
    nameKey: "games.library.soundMatch",
    descriptionKey: "games.library.soundMatchDesc",
    domains: ["attention", "memory"],
  },
  [GAME_IDS.targetFocus]: {
    nameKey: "games.library.targetFocus",
    descriptionKey: "games.library.targetFocusDesc",
    domains: ["attention"],
  },
  [GAME_IDS.pathMemory]: {
    nameKey: "games.library.pathMemory",
    descriptionKey: "games.library.pathMemoryDesc",
    domains: ["spatial", "memory"],
  },
  [GAME_IDS.categoryTap]: {
    nameKey: "games.library.categoryTap",
    descriptionKey: "games.library.categoryTapDesc",
    domains: ["language", "cognitive_flexibility"],
  },
  [GAME_IDS.objectRotationMatch]: {
    nameKey: "games.library.objectRotationMatch",
    descriptionKey: "games.library.objectRotationMatchDesc",
    domains: ["spatial"],
  },
  [GAME_IDS.rhythmTap]: {
    nameKey: "games.library.rhythmTap",
    descriptionKey: "games.library.rhythmTapDesc",
    domains: ["memory", "attention"],
  },
};

const GAME_IDS_VALUES = Object.values(GAME_IDS) as GameId[];
const GAME_IDS_LIST = GAME_IDS_VALUES.filter(
  (id, i, arr) => arr.indexOf(id) === i
);
const VOICE_SUPPORTED_GAME_IDS = new Set<string>([
  GAME_IDS.categoryTap,
  GAME_IDS.wordFluencyTap,
]);

/** Routes that exist for games (free-play launches here) */
const GAME_ROUTES: Record<string, string> = {
  [GAME_IDS.sequenceRecall]: "sequence-recall",
  [GAME_IDS.nBack]: "n-back",
  [GAME_IDS.wordFluencyTap]: "word-fluency-tap",
  [GAME_IDS.mathBlitz]: "math-blitz",
  [GAME_IDS.pathMemory]: "path-memory",
  [GAME_IDS.reactionTap]: "reaction-tap",
  [GAME_IDS.categoryTap]: "category-tap",
  [GAME_IDS.patternMatch]: "pattern-match",
  [GAME_IDS.ruleSwitch]: "rule-switch",
  [GAME_IDS.targetFocus]: "target-focus",
};

export interface GameLibraryProps {
  basePath?: string;
}

const GameLibrary: FC<GameLibraryProps> = ({ basePath = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const selectedPatient = useSelector(
    (state: RootState) => state.home.selectedPatient,
  );
  const isPatientUser = useSelector(
    (state: RootState) => state.auth.user?.isPatient === true,
  );
  const prefix = basePath ? `/${basePath}` : "";
  const gamesBase = `${prefix}/games`;

  const { data: libraryData } = useGetLibraryQuery(
    { patientId: selectedPatientId! },
    { skip: !selectedPatientId },
  );
  const [createSession, { isLoading: isCreating }] = useCreateSessionMutation();
  const [updatePatient, { isLoading: isSavingTiming }] =
    useUpdatePatientMutation();
  const [domainFilter, setDomainFilter] = useState<string>("all");
  const [voiceOnly, setVoiceOnly] = useState(false);
  const [sort, setSort] = useState<SortOption>("beneficial");
  const [playError, setPlayError] = useState<string | null>(null);
  const [imageAttemptByGameId, setImageAttemptByGameId] = useState<
    Record<string, number>
  >({});

  const gameTimingMode: GameTimingMode = resolveGameTimingMode(
    selectedPatient?.gameTimingMode,
  );
  const inGameMoreTimeAllowed =
    selectedPatient?.inGameMoreTimeAllowed === true;
  const manualDifficultyEnabled =
    selectedPatient?.manualDifficultyEnabled === true;
  const manualDifficultyLevel = Math.max(
    1,
    Math.min(10, selectedPatient?.manualDifficultyLevel ?? 5),
  );

  const handleGameTimingChange = useCallback(
    (_: unknown, checked: boolean) => {
      if (!selectedPatientId || !selectedPatient) return;
      const next: GameTimingMode = checked ? "relaxed" : "standard";
      updatePatient({
        id: selectedPatientId,
        gameTimingMode: next,
      })
        .unwrap()
        .then((updated) => {
          dispatch(setSelectedPatient(updated));
        })
        .catch(() => {});
    },
    [selectedPatientId, selectedPatient, updatePatient, dispatch],
  );

  const handleInGameMoreTimeChange = useCallback(
    (_: unknown, checked: boolean) => {
      if (!selectedPatientId || !selectedPatient) return;
      updatePatient({
        id: selectedPatientId,
        inGameMoreTimeAllowed: checked,
      })
        .unwrap()
        .then((updated) => {
          dispatch(setSelectedPatient(updated));
        })
        .catch(() => {});
    },
    [selectedPatientId, selectedPatient, updatePatient, dispatch],
  );

  const handleManualDifficultyEnabledChange = useCallback(
    (_: unknown, checked: boolean) => {
      if (!selectedPatientId || !selectedPatient) return;
      updatePatient({
        id: selectedPatientId,
        manualDifficultyEnabled: checked,
      })
        .unwrap()
        .then((updated) => {
          dispatch(setSelectedPatient(updated));
        })
        .catch(() => {});
    },
    [selectedPatientId, selectedPatient, updatePatient, dispatch],
  );

  const handleManualDifficultyLevelChange = useCallback(
    (delta: number) => {
      if (!selectedPatientId || !selectedPatient) return;
      const next = Math.max(1, Math.min(10, manualDifficultyLevel + delta));
      if (next === manualDifficultyLevel) return;
      updatePatient({
        id: selectedPatientId,
        manualDifficultyLevel: next,
      })
        .unwrap()
        .then((updated) => {
          dispatch(setSelectedPatient(updated));
        })
        .catch(() => {});
    },
    [
      selectedPatientId,
      selectedPatient,
      manualDifficultyLevel,
      updatePatient,
      dispatch,
    ],
  );

  const gamesList: (GameLibraryItem & { gameId: string })[] = useMemo(() => {
    const apiGames = libraryData?.games;
    if (apiGames?.length) {
      return apiGames.map((g) => {
        const meta = GAME_META[g.gameId];
        const name = meta ? t(meta.nameKey) : g.name;
        const description = meta ? t(meta.descriptionKey) : g.description ?? "";
        return { ...g, gameId: g.gameId, name, description };
      });
    }
    return GAME_IDS_LIST.map((gameId, i) => {
      const meta = GAME_META[gameId];
      return {
        gameId,
        name: meta ? t(meta.nameKey) : gameId,
        description: meta ? t(meta.descriptionKey) : "",
        domains: meta ? [...meta.domains] : [],
        iconUrl: "",
        userLevel: 1,
        lastPlayedAt: null,
        domainPriority: i + 1,
      };
    });
  }, [libraryData?.games, t]);

  const filtered = useMemo(() => {
    let list = [...gamesList];
    if (voiceOnly) {
      list = list.filter((g) => VOICE_SUPPORTED_GAME_IDS.has(g.gameId));
    }
    if (domainFilter !== "all") {
      list = list.filter((g) => g.domains.includes(domainFilter));
    }
    if (sort === "alphabetical") {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === "recent") {
      list.sort((a, b) => {
        const ta = a.lastPlayedAt ? new Date(a.lastPlayedAt).getTime() : 0;
        const tb = b.lastPlayedAt ? new Date(b.lastPlayedAt).getTime() : 0;
        return tb - ta;
      });
    } else {
      list.sort((a, b) => a.domainPriority - b.domainPriority);
    }
    return list;
  }, [gamesList, domainFilter, sort, voiceOnly]);

  const handlePlay = (gameId: string) => {
    const route = GAME_ROUTES[gameId];
    if (!route || !selectedPatientId) {
      return;
    }
    setPlayError(null);
    createSession({
      sessionType: "freeplay",
      gameId,
      patientId: selectedPatientId,
    })
      .unwrap()
      .then((res) => {
        navigate(`${gamesBase}/${route}`, {
          state: {
            freePlaySessionId: res.sessionId,
            freePlayGames: res.games,
          },
        });
      })
      .catch((err: { status?: number }) => {
        if (err?.status === 403) {
          setPlayError(t("games.errors.onlyPatientCanPlay"));
        } else {
          // Backend may not support v2.1 session API yet: still open game so it can use legacy session start
          navigate(`${gamesBase}/${route}`);
        }
      });
  };

  const pageContent = (
    <>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {t("games.subtitle")}
      </Typography>
      {selectedPatientId && selectedPatient && !isPatientUser && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={gameTimingMode === "relaxed"}
                onChange={handleGameTimingChange}
                disabled={isSavingTiming}
                inputProps={{
                  "aria-label": t("games.library.moreTimeForGames"),
                }}
              />
            }
            label={t("games.library.moreTimeForGames")}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {t("games.library.moreTimeForGamesHelper")}
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={inGameMoreTimeAllowed}
                onChange={handleInGameMoreTimeChange}
                disabled={isSavingTiming}
                inputProps={{
                  "aria-label": t("games.library.inGameMoreTime"),
                }}
              />
            }
            label={t("games.library.inGameMoreTime")}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {t("games.library.inGameMoreTimeHelper")}
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={manualDifficultyEnabled}
                onChange={handleManualDifficultyEnabledChange}
                disabled={isSavingTiming}
                inputProps={{
                  "aria-label": t("games.library.manualDifficulty"),
                }}
              />
            }
            label={t("games.library.manualDifficulty")}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {t("games.library.manualDifficultyHelper")}
          </Typography>
          {manualDifficultyEnabled && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mt: 1 }}
            >
              <IconButton
                onClick={() => handleManualDifficultyLevelChange(-1)}
                disabled={isSavingTiming || manualDifficultyLevel <= 1}
                aria-label={t("games.library.manualDifficultyDecrease")}
                sx={{ minWidth: 56, minHeight: 56 }}
              >
                <RemoveIcon fontSize="large" />
              </IconButton>
              <Box
                sx={{
                  minWidth: 72,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
                aria-live="polite"
              >
                <Typography variant="h4" fontWeight={600} lineHeight={1}>
                  {manualDifficultyLevel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("games.library.manualDifficultyLevelLabel")}
                </Typography>
              </Box>
              <IconButton
                onClick={() => handleManualDifficultyLevelChange(1)}
                disabled={isSavingTiming || manualDifficultyLevel >= 10}
                aria-label={t("games.library.manualDifficultyIncrease")}
                sx={{ minWidth: 56, minHeight: 56 }}
              >
                <AddIcon fontSize="large" />
              </IconButton>
            </Stack>
          )}
        </Box>
      )}
      {playError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {playError}
        </Typography>
      )}

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Chip
          label={t("games.library.filterAll")}
          onClick={() => setDomainFilter("all")}
          color={domainFilter === "all" ? "primary" : "default"}
          variant={domainFilter === "all" ? "filled" : "outlined"}
        />
        <Chip
          icon={<RecordVoiceOverIcon />}
          label={t("games.library.filterVoice")}
          onClick={() => setVoiceOnly((v) => !v)}
          color={voiceOnly ? "secondary" : "default"}
          variant={voiceOnly ? "filled" : "outlined"}
        />
        {DOMAIN_KEYS.map((key) => (
          <Chip
            key={key}
            label={t(`games.sessionSummary.domains.${key}`)}
            onClick={() => setDomainFilter(key)}
            color={domainFilter === key ? "primary" : "default"}
            variant={domainFilter === key ? "filled" : "outlined"}
          />
        ))}
      </Stack>

      <ToggleButtonGroup
        value={sort}
        exclusive
        onChange={(_, v) => v != null && setSort(v)}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="beneficial">
          {t("games.library.sortBeneficial")}
        </ToggleButton>
        <ToggleButton value="recent">
          {t("games.library.sortRecent")}
        </ToggleButton>
        <ToggleButton value="alphabetical">
          {t("games.library.sortAlphabetical")}
        </ToggleButton>
      </ToggleButtonGroup>

      <Grid container spacing={2}>
        {filtered.map((game) => {
          const route = GAME_ROUTES[game.gameId];
          const canPlay = Boolean(route);
          const showPlayButton = canPlay && basePath === "portal";
          const voiceSupported = VOICE_SUPPORTED_GAME_IDS.has(game.gameId);
          const imageCandidates = buildImageCandidateUrls(game.iconUrl, [
            `/games/icons/${game.gameId}.png`,
          ]);
          const imageAttempt = imageAttemptByGameId[game.gameId] ?? 0;
          const imageSrc =
            imageCandidates.length > 0
              ? imageCandidates[Math.min(imageAttempt, imageCandidates.length - 1)]
              : "";
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={game.gameId}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "box-shadow 0.2s, border-color 0.2s",
                  "&:hover": {
                    boxShadow: 2,
                    borderColor: "primary.main",
                  },
                }}
              >
                <CardContent
                  sx={{
                    pt: 2,
                    pb: 2,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" gap={1.5} sx={{ mb: 1.5 }}>
                    {imageSrc ? (
                      <Box
                        component="img"
                        src={imageSrc}
                        alt={game.name}
                        onError={() =>
                          setImageAttemptByGameId((prev) => ({
                            ...prev,
                            [game.gameId]: Math.min(
                              (prev[game.gameId] ?? 0) + 1,
                              Math.max(0, imageCandidates.length)
                            ),
                          }))
                        }
                        sx={{
                          width: { xs: "min(28vw, 112px)", sm: 96 },
                          height: { xs: "min(28vw, 112px)", sm: 96 },
                          borderRadius: 2,
                          border: "2px solid",
                          borderColor: "rgba(0, 0, 0, 0.12)",
                          objectFit: "contain",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: { xs: "min(28vw, 112px)", sm: 96 },
                          height: { xs: "min(28vw, 112px)", sm: 96 },
                          borderRadius: 2,
                          border: "2px solid",
                          borderColor: "rgba(0, 0, 0, 0.12)",
                          bgcolor: "action.hover",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography variant="h5" color="text.secondary" fontWeight={700}>
                          {game.name.slice(0, 1).toUpperCase()}
                        </Typography>
                      </Box>
                    )}
                    <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ pt: 0.25 }}>
                      {game.domains.slice(0, 2).map((d) => (
                        <Chip
                          key={d}
                          label={t(`games.sessionSummary.domains.${d}`)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.75rem" }}
                        />
                      ))}
                      {voiceSupported && (
                        <Chip
                          icon={<RecordVoiceOverIcon />}
                          label={t("games.library.voiceSupported")}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ fontSize: "0.75rem" }}
                        />
                      )}
                    </Stack>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem" }}>
                      {game.name}
                    </Typography>
                    <Chip
                      label={t("games.shell.level", { level: game.userLevel })}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {game.description}
                  </Typography>
                  {game.lastPlayedAt && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {t("games.library.lastPlayed", { date: new Date(game.lastPlayedAt).toLocaleDateString() })}
                    </Typography>
                  )}
                  {showPlayButton ? (
                    <Box
                      sx={{
                        mt: "auto",
                        pt: 1,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handlePlay(game.gameId)}
                        disabled={isCreating || !selectedPatientId}
                        sx={gamePrimaryButtonSx}
                      >
                        {t("games.play")}
                      </Button>
                    </Box>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: "auto", pt: 1 }}
                    >
                      {t("games.comingSoon")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </>
  );

  if (basePath === "portal") {
    return (
      <PortalPageWrapper titleKey="games.title">
        {pageContent}
      </PortalPageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HomePageTitle title={t("games.title")} />
      {pageContent}
    </PageWrapper>
  );
};

export default GameLibrary;
